import React, { useState } from 'react';
import { Card, ListGroup, Button, Spinner, Badge, Alert, Form } from 'react-bootstrap';
import { FaTrash, FaSync } from 'react-icons/fa';
import { fetchGuestyReservations, parseApiResponse, updateWebhookFilterMode } from './webhookService';
import SyncProgressBar from './SyncProgressBar';

export default function WebhookCard({ webhook, index, onDelete, isDeleting }) {
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncSuccess, setSyncSuccess] = useState(null);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [reservationCount, setReservationCount] = useState(0);
  const [reservationIds, setReservationIds] = useState([]);
  const [filterMode, setFilterMode] = useState(webhook.filter_mode || 'ALL');
  const [updatingFilterMode, setUpdatingFilterMode] = useState(false);
  const [filterModeError, setFilterModeError] = useState(null);
  const [filterModeSuccess, setFilterModeSuccess] = useState(null);

  const handleSyncReservations = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);
    setShowProgressBar(false);

    try {
      const response = await fetchGuestyReservations(webhook.client_id);
      const result = await parseApiResponse(response);

      if (result.success) {
        const count = result.data.count || result.data.reservationIds?.length || 0;
        const ids = result.data.reservationIds || [];
        setReservationCount(count);
        setReservationIds(ids);
        
        if (count > 0) {
          // Show progress bar to start syncing one by one
          setShowProgressBar(true);
        } else {
          setSyncSuccess("No reservations to sync");
          setSyncing(false);
        }
      } else {
        setSyncError(result.error || "Failed to sync reservations");
        setSyncing(false);
      }
    } catch (error) {
      console.error('Error syncing reservations:', error);
      setSyncError("An error occurred while syncing reservations");
      setSyncing(false);
    }
  };
  
  const handleSyncComplete = () => {
    setSyncing(false);
    setShowProgressBar(false);
    setSyncSuccess(`Successfully synced ${reservationCount} reservations`);
  };
  
  const handleSyncCancel = () => {
    setSyncing(false);
    setShowProgressBar(false);
    setSyncSuccess(`Sync cancelled after partially updating reservations`);
  };

  const handleFilterModeChange = async (newFilterMode) => {
    setUpdatingFilterMode(true);
    setFilterModeError(null);
    setFilterModeSuccess(null);

    try {
      const response = await updateWebhookFilterMode(webhook.webhook_id, newFilterMode);
      const result = await parseApiResponse(response);

      if (result.success) {
        setFilterMode(newFilterMode);
        setFilterModeSuccess(`Filter mode updated to ${newFilterMode}`);
        // Clear success message after 3 seconds
        setTimeout(() => setFilterModeSuccess(null), 3000);
      } else {
        setFilterModeError(result.error || "Failed to update filter mode");
      }
    } catch (error) {
      console.error('Error updating filter mode:', error);
      setFilterModeError("An error occurred while updating filter mode");
    } finally {
      setUpdatingFilterMode(false);
    }
  };

  return (
    <Card className="h-100 border-success">
      <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Integration #{index + 1}</h6>
        <Badge bg="light" text="dark">
          {webhook.webhook_account_id}
        </Badge>
      </Card.Header>
      <Card.Body>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <strong>Webhook ID:</strong> {webhook.webhook_id}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Webhook URL:</strong> {webhook.webhook_url}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Company ID:</strong> {webhook.company_id}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Company Name:</strong> {webhook.company_name}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Client ID:</strong> {webhook.client_id}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Created By:</strong> {webhook.webhook_created_by}
          </ListGroup.Item>
          <ListGroup.Item>
            <strong>Created Time:</strong> {new Date(webhook.created_time).toLocaleString()}
          </ListGroup.Item>
        </ListGroup>

        {/* Filter Mode Section */}
        <div className="mt-3 p-3 border rounded bg-light">
          <h6 className="mb-3">Filter Mode</h6>
          <Form>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id={`filter-all-${webhook.webhook_id}`}
                name={`filter-mode-${webhook.webhook_id}`}
                label="All Reservations"
                checked={filterMode === 'ALL'}
                onChange={() => handleFilterModeChange('ALL')}
                disabled={updatingFilterMode}
              />
              <Form.Check
                type="radio"
                id={`filter-tag-${webhook.webhook_id}`}
                name={`filter-mode-${webhook.webhook_id}`}
                label="Tag-Based Filtering (UrbanAide)"
                checked={filterMode === 'TAG_BASED'}
                onChange={() => handleFilterModeChange('TAG_BASED')}
                disabled={updatingFilterMode}
              />
            </div>
          </Form>
          
          {filterMode === 'TAG_BASED' && (
            <div className="mt-2">
              <small className="text-muted">
                <strong>Note:</strong> Only reservations with the "UrbanAide" tag (case insensitive) will be processed.
              </small>
            </div>
          )}
          
          {updatingFilterMode && (
            <div className="mt-2 d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <small className="text-muted">Updating filter mode...</small>
            </div>
          )}
          
          {filterModeSuccess && (
            <Alert variant="success" className="mt-2 mb-0 py-2">
              <small>{filterModeSuccess}</small>
            </Alert>
          )}
          
          {filterModeError && (
            <Alert variant="danger" className="mt-2 mb-0 py-2">
              <small>{filterModeError}</small>
            </Alert>
          )}
        </div>
        
        {syncSuccess && !showProgressBar && (
          <Alert variant="success" className="mt-3 mb-0">
            {syncSuccess}
          </Alert>
        )}
        
        {syncError && !showProgressBar && (
          <Alert variant="danger" className="mt-3 mb-0">
            {syncError}
          </Alert>
        )}
        
        {showProgressBar && (
          <SyncProgressBar 
            totalCount={reservationCount}
            reservationIds={reservationIds}
            onComplete={handleSyncComplete}
            onCancel={handleSyncCancel}
            clientId={webhook.client_id}
          />
        )}
        
        <div className="mt-3 d-flex gap-2">
          <Button 
            variant="primary" 
            onClick={handleSyncReservations}
            disabled={syncing}
            size="sm"
          >
            {syncing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Syncing...
              </>
            ) : (
              <>
                <FaSync className="me-2" /> Sync Reservations
              </>
            )}
          </Button>
          
          <Button 
            variant="danger" 
            onClick={() => onDelete(webhook.webhook_id)}
            disabled={isDeleting || syncing}
            size="sm"
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <FaTrash className="me-2" /> Delete Integration
              </>
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
